import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Pagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import MemberCard from '../../components/Members/MemberCard';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState, AppDispatch } from '../../store/store';
import { fetchMembers } from '../../store/slices/membersSlice';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

// Mapping of postal codes to full names (module-level constant for stable identity)
const STATE_CODE_TO_NAME: Record<string, string> = {
  'AL': 'Alabama','AK': 'Alaska','AZ': 'Arizona','AR': 'Arkansas','CA': 'California','CO': 'Colorado','CT': 'Connecticut','DE': 'Delaware','FL': 'Florida','GA': 'Georgia',
  'HI': 'Hawaii','ID': 'Idaho','IL': 'Illinois','IN': 'Indiana','IA': 'Iowa','KS': 'Kansas','KY': 'Kentucky','LA': 'Louisiana','ME': 'Maine','MD': 'Maryland',
  'MA': 'Massachusetts','MI': 'Michigan','MN': 'Minnesota','MS': 'Mississippi','MO': 'Missouri','MT': 'Montana','NE': 'Nebraska','NV': 'Nevada','NH': 'New Hampshire','NJ': 'New Jersey',
  'NM': 'New Mexico','NY': 'New York','NC': 'North Carolina','ND': 'North Dakota','OH': 'Ohio','OK': 'Oklahoma','OR': 'Oregon','PA': 'Pennsylvania','RI': 'Rhode Island','SC': 'South Carolina',
  'SD': 'South Dakota','TN': 'Tennessee','TX': 'Texas','UT': 'Utah','VT': 'Vermont','VA': 'Virginia','WA': 'Washington','WV': 'West Virginia','WI': 'Wisconsin','WY': 'Wyoming',
  'DC': 'District of Columbia','PR': 'Puerto Rico','GU': 'Guam','VI': 'U.S. Virgin Islands','AS': 'American Samoa','MP': 'Northern Mariana Islands'
};

const Members: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { members, pagination, loading, error } = useSelector((state: RootState) => state.members);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlSearch = params.get('q') || '';
  const rawState = (params.get('state') || '').trim();
  const stateFilter = STATE_CODE_TO_NAME[rawState.toUpperCase()] ? rawState.toUpperCase() :
    Object.entries(STATE_CODE_TO_NAME).find(([, name]) => name.toLowerCase() === rawState.toLowerCase())?.[0] || '';
  const [search, setSearch] = useState(urlSearch);
  const debouncedSearch = urlSearch.trim();
  const [pageState, setPageState] = useState({ key: location.search, page: 1 });
  const page = pageState.key === location.search ? pageState.page : 1;

  useEffect(() => { setSearch(urlSearch); }, [urlSearch]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim() === urlSearch) return;
      const next = new URLSearchParams(location.search);
      if (search.trim()) next.set('q', search.trim()); else next.delete('q');
      navigate({ search: next.toString() }, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, urlSearch, location.search, navigate]);

  const setStateFilter = (value: string) => {
    const next = new URLSearchParams(location.search);
    if (value) next.set('state', STATE_CODE_TO_NAME[value] || value); else next.delete('state');
    navigate({ search: next.toString() });
  };
  useEffect(() => {
    dispatch(fetchMembers({ limit: pagination.per_page, offset: (page - 1) * pagination.per_page,
      q: debouncedSearch || undefined, state: stateFilter || undefined }));
  }, [dispatch, page, pagination.per_page, debouncedSearch, stateFilter]);
  const handlePageChange = (_: React.ChangeEvent<unknown>, next: number) => {
    setPageState({ key: location.search, page: next });
  };

  const filteredMembers = useMemo(() => {
    const arr = members || [];
    const q = search.trim().toLowerCase();
    return arr.filter((m: any) => {
      // Apply state filter first if selected
      if (stateFilter) {
        const ms = String(m.state || '');
        const fullName = STATE_CODE_TO_NAME[stateFilter] || '';
        const matches = ms.toUpperCase() === stateFilter || ms.toLowerCase() === fullName.toLowerCase();
        if (!matches) return false;
      }
      if (!q) return true;
      const name = String(m.name || `${m.first_name || ''} ${m.last_name || ''}`).toLowerCase();
      const state = String(m.state ?? '').toLowerCase();
      const party = String(m.party ?? '').toLowerCase();
      const district = String(m.district ?? '').toLowerCase();
      const bioguide = String(m.bioguideId ?? m.bioguide_id ?? '').toLowerCase();
      return (
        name.includes(q) ||
        state.includes(q) ||
        party.includes(q) ||
        district.includes(q) ||
        bioguide.includes(q)
      );
    });
  }, [members, search, stateFilter]);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Congressional Members
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          sx={{ flex: 1, minWidth: 260 }}
          label="Search members by name, state, party, district, or Bioguide ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel id="member-state-label">Filter by State</InputLabel>
          <Select
            labelId="member-state-label"
            value={stateFilter}
            label="Filter by State"
            onChange={(e) => setStateFilter(String(e.target.value))}
          >
            <MenuItem value="">All States</MenuItem>
            {[
              'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','GU','VI','AS','MP'
            ].map((st, idx) => (
              <MenuItem key={idx} value={st}>{st}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading members..." />
      ) : (
        <>
          {error && <Typography role="alert" color="error">Could not load members. Please try again.</Typography>}
          {!error && filteredMembers.length === 0 && <Typography>No members found. Try another search or state.</Typography>}
          <Grid container spacing={2}>
            {filteredMembers.map((member, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <MemberCard member={member} />
              </Grid>
            ))}
          </Grid>

          {pagination.total > pagination.per_page && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.per_page)}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Members;
